/* Flooring Surgeons – PDP flooring calculator (m² → packs). No dependencies.
   packs = max(1, ceil(round(area × (1 + wastage/100) / packM2 × 1000) / 1000)); wastage = the switch (off = 0).
   It writes the pack count into the product form's quantity input and fires `change`; <fs-buy> (fs-pdp.js) then
   updates the stepper, total and sticky bar. Changing the packs (stepper, sticky bar or typing) writes back the room
   area those packs cover – with the wastage switch on, the area before the allowance – so box, packs and total always
   agree (owner, 10 Oct 2026). */
if (!customElements.get('fs-m2-calculator')) {
  const STORE_KEY = 'fs-calc-area'; // shared with fs-measure.js, fs-cart.js and fs-plp.js: { mode: 'area', area, waste }
  // Remembered for this visit only (sessionStorage) – owner, 27 Sep 2026: a room size typed days ago shouldn't pre-fill
  // every floor. Clears itself when the browser/tab session ends.
  const store = {
    get() { try { return JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch (e) { return null; } },
    set(v) { try { sessionStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) { /* storage unavailable */ } },
  };
  try { localStorage.removeItem(STORE_KEY); } catch (e) { /* old long-lived copy */ }
  // text box (no browser number spinner): accept "20,5" as well as "20.5", ignore letters
  const num = (v) => parseFloat(String(v || '').replace(',', '.').replace(/[^0-9.]/g, '')) || 0;

  customElements.define(
    'fs-m2-calculator',
    class FsM2Calculator extends HTMLElement {
      static packsFor(area, waste, packM2) {
        const needed = area * (1 + waste / 100);
        // round to 3dp first so floating-point noise can't add a pack (e.g. 0.96 × 21)
        return Math.max(1, Math.ceil(Math.round((needed / packM2) * 1000) / 1000));
      }

      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        this.packM2 = parseFloat(this.dataset.packM2);
        this.areaInput = this.querySelector('[data-area]');
        this.wasteInput = this.querySelector('[data-waste]');
        this.qty = document.getElementById(this.dataset.quantityInput);
        this.underlay = this.closest('fs-buy')?.querySelector('[data-underlay]');

        this.areaInput.addEventListener('input', () => this.update(true));
        this.wasteInput.addEventListener('change', () => this.update(true));
        if (this.qty) ['input', 'change'].forEach((t) => this.qty.addEventListener(t, () => this.fromPacks()));
        const info = this.querySelector('[data-info]');
        const tip = this.querySelector('[data-info-text]');
        info.addEventListener('click', () => {
          const open = info.getAttribute('aria-expanded') !== 'true';
          info.setAttribute('aria-expanded', open);
          tip.hidden = !open;
        });

        const saved = store.get(); // the customer's room this visit (this calculator, the measuring guide or the PLP calculator)
        let area = saved && parseFloat(saved.area);
        if (!(area > 0)) {
          try { area = parseFloat((JSON.parse(sessionStorage.getItem('fs-room')) || {}).m2); } catch (e) { area = 0; }
        }
        if (saved && saved.mode === 'room' && Array.isArray(saved.rooms)) {
          area = saved.rooms.reduce((sum, [l, w]) => sum + (parseFloat(l) || 0) * (parseFloat(w) || 0), 0); // older room-size entries
        }
        if (area > 0) {
          this.areaInput.value = Math.round(area * 100) / 100;
          // a room saved with the wastage switch on (here or in the category-page calculator) keeps it on
          if (saved && saved.waste === true) this.wasteInput.checked = true;
          this.update();
        }
      }

      update(remember) {
        const area = num(this.areaInput.value);
        if (remember) {
          const waste = this.wasteInput.checked;
          store.set({ mode: 'area', area: area || '', waste });
          // keep the category-page calculator in step (it shows totals on every card, with the same wastage choice)
          try {
            if (area > 0) sessionStorage.setItem('fs-room', JSON.stringify({ m2: area, name: '', mode: 'area', unit: 'm', waste, raw: { area: String(area) } }));
            else sessionStorage.removeItem('fs-room');
          } catch (e) { /* storage unavailable */ }
        }
        const ok = area > 0 && this.packM2 > 0;
        if (this.underlay) {
          this.underlay.hidden = !ok || this.dataset.floating !== 'true';
          const out = this.underlay.querySelector('[data-underlay-m2]');
          if (out) out.textContent = Math.ceil(area);
        }
        if (!ok || !this.qty) return;
        const packs = FsM2Calculator.packsFor(area, this.wasteInput.checked ? parseFloat(this.wasteInput.value) || 0 : 0, this.packM2);
        if (String(this.qty.value) !== String(packs)) {
          this.qty.value = packs;
          this.writing = true;
          this.qty.dispatchEvent(new Event('change', { bubbles: true }));
          this.writing = false;
        }
      }

      // packs → area: the largest 2-dp room area that still works out to exactly these packs
      fromPacks() {
        if (this.writing || !(this.packM2 > 0)) return;
        const packs = parseInt(this.qty.value, 10);
        if (!(packs > 0)) return;
        const waste = this.wasteInput.checked ? parseFloat(this.wasteInput.value) || 0 : 0;
        let area = Math.floor(Math.round(((packs * this.packM2) / (1 + waste / 100)) * 1e6) / 1e4) / 100;
        while (area > 0.01 && FsM2Calculator.packsFor(area, waste, this.packM2) > packs) area = Math.round((area - 0.01) * 100) / 100;
        if (num(this.areaInput.value) === area) return;
        this.areaInput.value = area.toFixed(2);
        this.update(true);
      }
    }
  );
}
